import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"

import type { ScheduledMeeting } from "@/api/types"
import { logoutAction } from "@/store/session/logoutAction"
import type { RootState } from "@/store/store"
import { hasMeetingEnded } from "@/utils/meetingStatus"

interface UpcomingState {
  meetings: ScheduledMeeting[]
}

const sortUpcomingMeetings = (meetings: ScheduledMeeting[]) =>
  [...meetings].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
  )

const initialState: UpcomingState = {
  meetings: [],
}

const upcomingSlice = createSlice({
  name: "upcoming",
  initialState,
  reducers: {
    setUpcomingMeetings: (state, action: PayloadAction<ScheduledMeeting[]>) => {
      state.meetings = sortUpcomingMeetings(action.payload)
    },
    addUpcomingMeeting: (state, action: PayloadAction<ScheduledMeeting>) => {
      state.meetings = sortUpcomingMeetings([action.payload, ...state.meetings])
    },
    updateUpcomingMeeting: (state, action: PayloadAction<ScheduledMeeting>) => {
      const index = state.meetings.findIndex(
        (m) => m.meetId === action.payload.meetId,
      )
      if (index !== -1) {
        state.meetings[index] = action.payload
        state.meetings = sortUpcomingMeetings(state.meetings)
      }
    },
    removeUpcomingMeeting: (state, action: PayloadAction<string>) => {
      state.meetings = state.meetings.filter((m) => m.meetId !== action.payload)
    },
    pruneExpiredUpcomingMeetings: (state, action: PayloadAction<number>) => {
      state.meetings = state.meetings.filter(
        (meeting) =>
          !hasMeetingEnded(
            meeting.startAt,
            meeting.durationMinutes,
            action.payload,
          ),
      )
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => initialState)
  },
})

export const {
  setUpcomingMeetings,
  addUpcomingMeeting,
  updateUpcomingMeeting,
  removeUpcomingMeeting,
  pruneExpiredUpcomingMeetings,
} = upcomingSlice.actions

export const selectUpcomingMeetings = (state: RootState) =>
  state.upcoming.meetings

export default upcomingSlice.reducer
